<template>
    <div
        class="filters"
        :class="{
            'report-filters': styleType === 'report',
            'segment-filters': styleType === 'segment',
            'full-width': styleType === 'segment' && filters.length > 0,
        }">
        <ul
            v-if="filters.length"
            role="list"
            class="filters__list"
            :class="{
                'report-filters__list': styleType === 'report',
                'segment-filters__list': styleType === 'segment',
            }"
            :aria-label="styleType === 'report' ? $t('eventPropertyFilter.Active report filters') : undefined">
            <li
                v-for="(filter, idx) in filters"
                :key="`${filter.id}-filter`"
                ref="filterRows"
                role="listitem"
                class="filters__filter">
                <div
                    role="group"
                    :aria-label="getFilterAriaLabel(filter, idx)">
                    <event-property-filter
                        :app-ids="appIds"
                        :filter="filter"
                        :disabled="disabled"
                        :target-entities="targetEntities"
                        :event-property-options="eventPropertyOptions"
                        :historical-metadata="historicalMetadata"
                        :session-properties="sessionProperties"
                        :account-metadata="accountMetadata"
                        :visitor-metadata="visitorMetadata"
                        :parent-account-metadata="parentAccountMetadata"
                        :metadata="metadata"
                        :selected-date-range="selectedDateRange"
                        @save="saveFilter"
                        @delete="deleteFilter"
                        @filter-complete="onFilterComplete"
                        @filter-incomplete="onFilterIncomplete" />
                </div>
                <span
                    v-if="idx !== filters.length - 1"
                    aria-hidden="true"
                    class="filters__and">
                    {{ $t('eventPropertyFilter.And') }}
                </span>
            </li>
        </ul>
        <div
            role="status"
            aria-live="polite"
            aria-atomic="true"
            class="sr-only">
            {{ liveRegionMessage }}
        </div>
        <pendo-button
            v-if="canAddFilters"
            ref="addFilterButton"
            class="add-filter-button"
            role="button"
            :aria-expanded="addFilterMenuOpen"
            :disabled="hasInvalidFilter || disabled"
            data-cy="event-property-add-filter"
            :label="addFilterButtonLabel"
            :aria-label="addFilterAriaLabel"
            theme="app"
            :type="addFilterButtonType"
            prefix-icon="plus"
            :size="addFilterButtonSize"
            @click="saveFilter()" />
    </div>
</template>

<script>
import EventPropertyFilter from './event-property-filter.vue';
import { PendoButton } from '@pendo/components';
import { getFirstFocusableNode } from '@/utils/dom.utils';
import { isFilterValid } from './utils';
import { eventPropNames, setupEventProperties } from './utils';
import { getAppIdsFromTargets } from '@/constants/device-props';
import { filterEventPropList } from '@/vuex/modules/metadata/metadata.utils';
import { EventKind, PageParameterRuleValue } from '@/types/analytics/analytics.types';

export default {
    name: 'EventPropertyFilterSection',
    components: {
        EventPropertyFilter,
        PendoButton,
    },
    props: {
        targetEntities: {
            type: Array,
            default: () => [],
        },
        filters: {
            type: Array,
            default: () => [],
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        styleType: {
            type: String,
            default: 'event',
        },
        enableVisitorAccountMetadata: {
            type: Boolean,
            default: false,
        },
        metadata: {
            type: Object,
            default: () => ({}),
        },
        historicalMetadata: {
            type: Array,
            default: () => [],
        },
        sessionProperties: {
            type: Array,
            default: () => [],
        },
        accountMetadata: {
            type: Array,
            default: () => [],
        },
        visitorMetadata: {
            type: Array,
            default: () => [],
        },
        parentAccountMetadata: {
            type: Array,
            default: () => [],
        },
        isWebAnalytics: {
            type: Boolean,
            default: false,
        },
        includePageParameter: {
            type: Boolean,
            default: false,
        },
        includeLocationData: {
            type: Boolean,
            default: false,
        },
        includeServerData: {
            type: Boolean,
            default: false,
        },
        selectedDateRange: {
            type: Object,
            default: () => ({}),
        },
        maxFilters: {
            type: Number,
            default: Infinity,
        },
        addFilterMenuOpen: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['delete', 'save', 'filter-complete'],
    data () {
        return {
            liveRegionText: '',
            previousValidFilterCount: 0,
            announceToggler: false,
            pendingValidFilters: new Set(),
        };
    },
    computed: {
        addFilterButtonLabel () {
            if (this.styleType === 'report') {
                return this.$t('eventPropertyFilter.Add report filter');
            }

            if (this.styleType === 'apps') {
                return this.$t('eventPropertyFilter.Add metadata filter');
            }

            if (this.styleType === 'segment') {
                return this.$t('eventPropertyFilter.Use event property');
            }

            return this.$t('eventPropertyFilter.Add filter');
        },
        addFilterButtonSize () {
            if (this.styleType === 'report' || this.styleType === 'apps') {
                return 'small';
            }

            if (this.styleType === 'segment') {
                return 'medium';
            }

            return 'mini';
        },
        addFilterButtonType () {
            if (this.styleType === 'report' || this.styleType === 'apps') {
                return 'secondary';
            }

            if (this.styleType === 'segment') {
                return 'link';
            }

            return 'link';
        },
        canAddFilters () {
            return this.filters.length < this.maxFilters;
        },
        hasInvalidFilter () {
            return !this.filters.every(isFilterValid);
        },
        filteredEventPropList () {
            return filterEventPropList({
                metadata: this.metadata,
                epNames: eventPropNames(this.targetEntities),
                uniqueBetweenTypes: true,
                trimUniqueValues: true,
            });
        },
        pagesWithPageParams () {
            const pages = [];
            if (!this.includePageParameter) {
                return [];
            }
            this.targetEntities.forEach((target) => {
                if (target.kind === EventKind.Page) {
                    const hasPageParameterRule = target.rules.some(
                        (rule) => rule.rule.indexOf(PageParameterRuleValue) > -1,
                    );
                    if (hasPageParameterRule) {
                        pages.push(target);
                    }
                }
            });

            return pages;
        },
        eventPropertyOptions () {
            const utmMetadata = this.sessionProperties;
            const accountMetadata = this.enableVisitorAccountMetadata ? this.accountMetadata : [];
            const visitorMetadata = this.enableVisitorAccountMetadata ? this.visitorMetadata : [];
            const parentAccountMetadata = this.enableVisitorAccountMetadata ? this.parentAccountMetadata : [];

            return setupEventProperties({
                targets: this.targetEntities,
                eventProperties: this.filteredEventPropList,
                historicalMetadata: this.historicalMetadata,
                sessionProperties: utmMetadata,
                accountMetadata,
                visitorMetadata,
                parentAccountMetadata,
                setupForWebAnalytics: this.isWebAnalytics,
                pagesWithPageParams: this.pagesWithPageParams,
                includeLocation: this.includeLocationData,
                includeServer: this.includeServerData,
            });
        },
        appIds () {
            return getAppIdsFromTargets(this.targetEntities);
        },
        liveRegionMessage () {
            return this.liveRegionText;
        },
        addFilterAriaLabel () {
            if (this.styleType === 'report') {
                return this.$t('eventPropertyFilter.Add filter to Report filters');
            }

            return this.addFilterButtonLabel;
        },
    },
    watch: {
        filters: {
            handler (newFilters, oldFilters) {
                const newValidCount = newFilters.filter(isFilterValid).length;

                if (oldFilters === undefined || oldFilters === null) {
                    this.previousValidFilterCount = newValidCount;

                    return;
                }

                if (newFilters.length > oldFilters.length) {
                    this.$nextTick(() => {
                        this.focusFilterRow(newFilters.length - 1);
                    });
                }

                if (newValidCount > this.previousValidFilterCount) {
                    // Filter became valid (X button would appear)
                    // Track which filters are newly valid - we'll announce when dropdowns close
                    const newValidFilters = newFilters.filter(isFilterValid);
                    const oldValidFilterIds = oldFilters ? oldFilters.filter(isFilterValid).map((filter) => filter.id) : [];
                    newValidFilters.forEach((filter) => {
                        if (!oldValidFilterIds.includes(filter.id)) {
                            this.pendingValidFilters.add(filter.id);
                        }
                    });
                } else if (newValidCount < this.previousValidFilterCount) {
                    // Check if a filter was actually deleted (removed from array) vs just became invalid
                    const oldFilterIds = oldFilters ? oldFilters.map((filter) => filter.id) : [];
                    const newFilterIds = newFilters.map((filter) => filter.id);
                    const deletedFilterIds = oldFilterIds.filter((id) => !newFilterIds.includes(id));

                    // Only announce "Filter removed" if a filter was actually deleted from the array
                    // Don't announce if filters are still present but just became invalid
                    if (deletedFilterIds.length > 0) {
                        this.announceToggler = !this.announceToggler;
                        const zwc = this.announceToggler ? '\u200B' : '\u200C';
                        if (this.styleType === 'report') {
                            this.liveRegionText = `${zwc}${this.$t('eventPropertyFilter.Report filter removed')}${zwc}`;
                        } else {
                            this.liveRegionText = `${zwc}${this.$t('eventPropertyFilter.Filter removed')}${zwc}`;
                        }
                        this.$nextTick(() => {
                            setTimeout(() => {
                                this.liveRegionText = '';
                            }, 1000);
                        });
                    }
                }

                this.previousValidFilterCount = newValidCount;
            },
            immediate: true,
            deep: true,
        },
    },
    methods: {
        deleteFilter ($event) {
            this.$emit('delete', $event);
            this.focusAddFilterButton();
        },
        saveFilter ($event) {
            if (!$event?.filter) {
                $event = {
                    filter: {
                        field: '',
                        operator: '',
                        kind: '',
                        filterType: '',
                        comparisonValues: [],
                    },
                };
            }
            this.$emit('save', $event);
        },
        getFilterAriaLabel (filter, index) {
            const filterNumber = index + 1;

            if (this.styleType === 'report') {
                return this.$t('eventPropertyFilter.Report filter {index}', {
                    index: filterNumber,
                });
            }

            return this.$t('eventPropertyFilter.Filter {index}', {
                index: filterNumber,
            });
        },

        focusFilterRow (index) {
            const rows = this.$refs.filterRows;
            const row = Array.isArray(rows) ? rows[index] : rows;
            const el = row?.$el ?? row;
            const node = el && getFirstFocusableNode(el);
            if (node) {
                node.focus();
            }
        },
        focusAddFilterButton () {
            const btn = this.$refs.addFilterButton;
            const el = btn?.$el ?? btn;
            if (!el) return;
            const node = getFirstFocusableNode(el) ?? (typeof el.focus === 'function' ? el : null);
            if (node) {
                node.focus();
            }
        },
        onFilterComplete ({ filter }) {
            this.$emit('filter-complete', { filter });
            if (this.pendingValidFilters.has(filter.id)) {
                this.pendingValidFilters.delete(filter.id);
                this.announceToggler = !this.announceToggler;
                const zwc = this.announceToggler ? '\u200B' : '\u200C';
                if (this.styleType === 'report') {
                    this.liveRegionText = `${zwc}${this.$t('eventPropertyFilter.Report filter added')}${zwc}`;
                } else {
                    this.liveRegionText = `${zwc}${this.$t('eventPropertyFilter.Filter added')}${zwc}`;
                }
                this.$nextTick(() => {
                    setTimeout(() => {
                        this.liveRegionText = '';
                    }, 1000);
                });
            }
        },
        async onFilterIncomplete () {
            await this.$nextTick();
            this.focusAddFilterButton();
        },
    },
};
</script>

<style lang="less" scoped>
.filters {
    margin-top: 8px;
    display: grid;

    ul {
        padding-inline-start: 0;
        list-style: none;
    }

    &__list {
        display: grid;
    }

    &__and {
        display: inline-block;
        font-size: 12px;
        font-weight: 600;
        color: var(--palette-gray-70);
        padding: 8px 0;
    }

    :deep(.pendo-button) {
        min-width: auto;
    }

    .add-filter-button {
        text-decoration: none;
        justify-self: start;

        &.pendo-button--mini {
            font-size: 12px;
        }
    }
}

.report-filters {
    margin-top: 0px;

    &__list {
        overflow: hidden;
        margin-bottom: 16px;
    }
}

.segment-filters {
    margin-top: 0px;

    &__list {
        overflow: hidden;
        margin-bottom: 0;
    }
}

.full-width {
    width: 100%;
}
</style>
